import React, { useEffect, useState, useMemo } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import axiosInstance from "../../config/axiosConfig";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  List,
  ListItemButton,
  ListItemText,
  Badge,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PublicIcon from '@mui/icons-material/Public';

export default function Users() {
  // ==========================================
  // ESTADOS PRINCIPALES DE DATOS
  // ==========================================
  const [users, setUsers] = useState([]);
  const [rolesSummary, setRolesSummary] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null); // null = "Todos"
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Catálogos para el formulario
  const [catalogs, setCatalogs] = useState({
    institutions: [],
    jobs: [],
    roles: [],
    applications: [],
    departments: [],
    municipalities: []
  });

  // ==========================================
  // ESTADOS DEL DIALOG (FORMULARIO)
  // ==========================================
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Formulario - Sección 1: Datos Generales
  const [firstName, setFirstName] = useState('');
  const [secondName, setSecondName] = useState('');
  const [thirdName, setThirdName] = useState('');
  const [firstSurname, setFirstSurname] = useState('');
  const [secondSurname, setSecondSurname] = useState('');
  const [marriedSurname, setMarriedSurname] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedInstitutionId, setSelectedInstitutionId] = useState('');
  const [selectedJobId, setSelectedJobId] = useState('');

  // Formulario - Sección 2: Datos de Acceso
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [selectedRoleIdForm, setSelectedRoleIdForm] = useState('');
  const [enabled, setEnabled] = useState(1);
  const [selectedAppIds, setSelectedAppIds] = useState([]);

  // Formulario - Asignaciones Geográficas
  const [selectedDeptosRegional, setSelectedDeptosRegional] = useState([]); // Rol 3
  const [selectedDeptoSingle, setSelectedDeptoSingle] = useState(''); // Rol 4
  const [filterDeptoForMuni, setFilterDeptoForMuni] = useState(''); // Rol 5 y 6 (Padre)
  const [selectedMunisMultiple, setSelectedMunisMultiple] = useState([]); // Rol 5
  const [selectedMuniSingle, setSelectedMuniSingle] = useState(''); // Rol 6

  // Notificaciones Snackbar
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  // ==========================================
  // CARGAS INICIALES
  // ==========================================
  const fetchRolesSummary = async () => {
    try {
      const res = await axiosInstance.get('/User/roles-summary');
      setRolesSummary(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error al cargar roles:', err);
    }
  };

  const fetchUsers = async (roleId = null) => {
    setLoading(true);
    try {
      const url = roleId ? `/User?roleId=${roleId}` : '/User';
      const res = await axiosInstance.get(url);
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
      setToast({ open: true, message: 'Error al cargar lista de usuarios.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalogs = async () => {
    try {
      const res = await axiosInstance.get('/User/form-catalogs');
      setCatalogs(res.data);
    } catch (err) {
      console.error('Error al cargar catálogos:', err);
    }
  };

  useEffect(() => {
    fetchRolesSummary();
    fetchUsers(null);
    fetchCatalogs();
  }, []);

  // Total de usuarios general sumando los conteos de roles
  const totalUsersCount = useMemo(() => {
    return rolesSummary.reduce((acc, curr) => acc + curr.userCount, 0);
  }, [rolesSummary]);

  // Manejar clic en rol del panel izquierdo
  const handleRoleSelect = (roleId) => {
    setSelectedRoleId(roleId);
    fetchUsers(roleId);
  };

  // Filtrado reactivo en el buscador de texto
  const filteredUsers = useMemo(() => {
    if (!searchText.trim()) return users;
    const lower = searchText.toLowerCase();
    return users.filter((u) =>
      (u.username || '').toLowerCase().includes(lower) ||
      (u.fullName || '').toLowerCase().includes(lower) ||
      (u.email || '').toLowerCase().includes(lower) ||
      (u.phone || '').toLowerCase().includes(lower) ||
      (u.job || '').toLowerCase().includes(lower) ||
      (u.roleName || '').toLowerCase().includes(lower) ||
      (u.applications || '').toLowerCase().includes(lower)
    );
  }, [users, searchText]);

  // Puestos filtrados por la Institución seleccionada
  const filteredJobs = useMemo(() => {
    if (!selectedInstitutionId) return catalogs.jobs;
    return catalogs.jobs.filter((j) => j.parentId === Number(selectedInstitutionId));
  }, [catalogs.jobs, selectedInstitutionId]);

  // Municipios filtrados por el Departamento seleccionado (para roles 5 y 6)
  const filteredMunicipalities = useMemo(() => {
    if (!filterDeptoForMuni) return [];
    return catalogs.municipalities.filter((m) => m.parentId === Number(filterDeptoForMuni));
  }, [catalogs.municipalities, filterDeptoForMuni]);

  // ==========================================
  // APERTURA DE MODAL (CREAR / EDITAR)
  // ==========================================
  const resetForm = () => {
    setFirstName('');
    setSecondName('');
    setThirdName('');
    setFirstSurname('');
    setSecondSurname('');
    setMarriedSurname('');
    setEmail('');
    setPhone('');
    setSelectedInstitutionId('');
    setSelectedJobId('');
    setUsername('');
    setPassword('');
    setSelectedRoleIdForm('');
    setEnabled(1);
    setSelectedAppIds([]);
    setSelectedDeptosRegional([]);
    setSelectedDeptoSingle('');
    setFilterDeptoForMuni('');
    setSelectedMunisMultiple([]);
    setSelectedMuniSingle('');
    setIsEditing(false);
    setEditingUserId(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setOpenModal(true);
  };

  const handleOpenEdit = async (id) => {
    resetForm();
    setIsEditing(true);
    setEditingUserId(id);
    setOpenModal(true);

    try {
      const res = await axiosInstance.get(`/User/${id}`);
      const data = res.data;

      setFirstName(data.firstName || '');
      setSecondName(data.secondName || '');
      setThirdName(data.thirdName || '');
      setFirstSurname(data.firstSurname || '');
      setSecondSurname(data.secondSurname || '');
      setMarriedSurname(data.marriedSurname || '');
      setEmail(data.email || '');
      setPhone(data.phone || '');
      setSelectedInstitutionId(data.idInstitution ? String(data.idInstitution) : '');
      setSelectedJobId(data.idJob ? String(data.idJob) : '');

      setUsername(data.username || '');
      setPassword(''); // Password queda vacío por seguridad
      setSelectedRoleIdForm(data.idRol ? String(data.idRol) : '');
      setEnabled(data.enable);
      setSelectedAppIds(data.applicationIds || []);

      // Poblar geografía según el rol
      const rId = data.idRol;
      if (rId === 3) {
        setSelectedDeptosRegional(data.departmentIds || []);
      } else if (rId === 4) {
        setSelectedDeptoSingle(data.departmentIds?.[0] ? String(data.departmentIds[0]) : '');
      } else if (rId === 5) {
        setFilterDeptoForMuni(data.selectedDepartmentId ? String(data.selectedDepartmentId) : '');
        setSelectedMunisMultiple(data.municipalityIds || []);
      } else if (rId === 6) {
        setFilterDeptoForMuni(data.selectedDepartmentId ? String(data.selectedDepartmentId) : '');
        setSelectedMuniSingle(data.municipalityIds?.[0] ? String(data.municipalityIds[0]) : '');
      }
    } catch (err) {
      console.error('Error al cargar detalle de usuario:', err);
      setToast({ open: true, message: 'No se pudo cargar el usuario para edición.', severity: 'error' });
      setOpenModal(false);
    }
  };

  // ==========================================
  // MANEJO DE CAMBIOS EN FORMULARIO
  // ==========================================
  const handleAppToggle = (appId) => {
    setSelectedAppIds((prev) =>
      prev.includes(appId) ? prev.filter((id) => id !== appId) : [...prev, appId]
    );
  };

  const handleDeptoRegionalToggle = (deptoId) => {
    setSelectedDeptosRegional((prev) =>
      prev.includes(deptoId) ? prev.filter((id) => id !== deptoId) : [...prev, deptoId]
    );
  };

  const handleMuniMultipleToggle = (muniId) => {
    setSelectedMunisMultiple((prev) =>
      prev.includes(muniId) ? prev.filter((id) => id !== muniId) : [...prev, muniId]
    );
  };

  // Al cambiar de rol en el formulario, limpiamos datos geográficos incompatibles
  const handleRoleFormChange = (e) => {
    setSelectedRoleIdForm(e.target.value);
    setSelectedDeptosRegional([]);
    setSelectedDeptoSingle('');
    setFilterDeptoForMuni('');
    setSelectedMunisMultiple([]);
    setSelectedMuniSingle('');
  };

  // ==========================================
  // GUARDAR (CREAR O MODIFICAR)
  // ==========================================
  const handleSaveUser = async () => {
    // Validaciones básicas de frontend
    if (!firstName.trim()) {
      setToast({ open: true, message: 'El primer nombre es obligatorio.', severity: 'warning' });
      return;
    }
    if (!firstSurname.trim()) {
      setToast({ open: true, message: 'El primer apellido es obligatorio.', severity: 'warning' });
      return;
    }
    if (!selectedJobId) {
      setToast({ open: true, message: 'Debe seleccionar un puesto de trabajo.', severity: 'warning' });
      return;
    }
    if (!username.trim()) {
      setToast({ open: true, message: 'El nombre de usuario es obligatorio.', severity: 'warning' });
      return;
    }
    if (!isEditing && !password.trim()) {
      setToast({ open: true, message: 'La contraseña es obligatoria para nuevos usuarios.', severity: 'warning' });
      return;
    }
    if (!selectedRoleIdForm) {
      setToast({ open: true, message: 'Debe seleccionar un rol para el usuario.', severity: 'warning' });
      return;
    }

    // Armar asignaciones geográficas según el rol
    const rId = Number(selectedRoleIdForm);
    let departmentIds = [];
    let municipalityIds = [];

    if (rId === 3) {
      if (selectedDeptosRegional.length < 2) {
        setToast({ open: true, message: 'Rol Regional: Debe seleccionar al menos 2 departamentos.', severity: 'warning' });
        return;
      }
      if (selectedDeptosRegional.length >= 22) {
        setToast({ open: true, message: 'Rol Regional: No puede seleccionar todos los departamentos (use rol Nacional).', severity: 'warning' });
        return;
      }
      departmentIds = selectedDeptosRegional;
    } else if (rId === 4) {
      if (!selectedDeptoSingle) {
        setToast({ open: true, message: 'Rol Departamental: Debe seleccionar un departamento.', severity: 'warning' });
        return;
      }
      departmentIds = [Number(selectedDeptoSingle)];
    } else if (rId === 5) {
      if (selectedMunisMultiple.length === 0) {
        setToast({ open: true, message: 'Rol Intermunicipal: Debe seleccionar al menos un municipio.', severity: 'warning' });
        return;
      }
      municipalityIds = selectedMunisMultiple;
    } else if (rId === 6) {
      if (!selectedMuniSingle) {
        setToast({ open: true, message: 'Rol Municipal: Debe seleccionar un municipio.', severity: 'warning' });
        return;
      }
      municipalityIds = [Number(selectedMuniSingle)];
    }

    const payload = {
      firstName: firstName.trim(),
      secondName: secondName.trim() || null,
      thirdName: thirdName.trim() || null,
      firstNameSurname: firstSurname.trim(),
      secondSurname: secondSurname.trim() || null,
      marriedSurname: marriedSurname.trim() || null,
      email: email.trim() || null,
      phone: phone.trim() || null,
      idJob: Number(selectedJobId),
      username: username.trim(),
      password: password.trim() ? password.trim() : null,
      idRol: rId,
      enable: enabled,
      applicationIds: selectedAppIds,
      departmentIds,
      municipalityIds
    };

    setSubmitting(true);
    try {
      if (isEditing) {
        await axiosInstance.put(`/User/${editingUserId}`, payload);
        setToast({ open: true, message: 'Usuario actualizado exitosamente.', severity: 'success' });
      } else {
        await axiosInstance.post('/User', payload);
        setToast({ open: true, message: 'Usuario creado exitosamente.', severity: 'success' });
      }

      setOpenModal(false);
      fetchUsers(selectedRoleId);
      fetchRolesSummary();
    } catch (err) {
      const msg = err.response?.data?.message || 'Error al guardar el usuario.';
      setToast({ open: true, message: msg, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // ==========================================
  // CONFIGURACIÓN DE COLUMNAS DATAGRID
  // ==========================================
  const columns = [
    { field: 'username', headerName: 'Usuario', width: 130 },
    { field: 'fullName', headerName: 'Nombre', minWidth: 200, flex: 1.2 },
    { field: 'email', headerName: 'Correo', minWidth: 190, flex: 1 },
    { field: 'phone', headerName: 'Teléfono', width: 120 },
    {
      field: 'enable',
      headerName: 'Estado',
      width: 130,
      renderCell: (params) =>
        params.value === 1 ? (
          <Chip icon={<CheckCircleIcon />} label="Habilitado" color="success" size="small" variant="outlined" />
        ) : (
          <Chip icon={<CancelIcon />} label="Deshabilitado" color="default" size="small" variant="outlined" />
        )
    },
    { field: 'job', headerName: 'Puesto', minWidth: 160, flex: 1 },
    {
      field: 'roleName',
      headerName: 'Rol',
      width: 140
    },
    {
      field: 'applications',
      headerName: 'Aplicaciones',
      minWidth: 160,
      flex: 1,
      renderCell: (params) =>
        params.value ? (
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#1976d2' }}>
            {params.value}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Ninguna
          </Typography>
        )
    },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 90,
      sortable: false,
      align: 'center',
      renderCell: (params) => (
        <Tooltip title="Editar usuario">
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleOpenEdit(params.row.id)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )
    }
  ];

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: "#f4f6f8",
        minHeight: "calc(100vh - 120px)",
      }}
    >
      {/* Barra superior de título y nuevo usuario */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <div>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#1a202c" }}>
            Usuarios
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Administración de cuentas, roles, permisos y cobertura geográfica.
          </Typography>
        </div>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          sx={{ backgroundColor: "#1976d2", textTransform: "none", px: 3 }}
        >
          Nuevo Usuario
        </Button>
      </Box>

      {/* Contenedor Principal: Panel Izquierdo (Roles) + Tabla Principal */}
        <Grid container spacing={3}>
        {/* PANEL IZQUIERDO: ROLES */}
        <Grid item xs={12} md={3.5} lg={3}>
            <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ p: 2, backgroundColor: '#1976d2', color: 'white' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                <SecurityIcon fontSize="small" /> Roles
                </Typography>
            </Box>

            <List disablePadding sx={{ py: 1 }}>
                {/* Opción para Ver Todos */}
                <ListItemButton
                selected={selectedRoleId === null}
                onClick={() => handleRoleSelect(null)}
                sx={{
                    py: 1.2,
                    px: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    '&.Mui-selected': { backgroundColor: '#e3f2fd', borderLeft: '4px solid #1976d2' }
                }}
                >
                <ListItemText
                    primary="Todos los usuarios"
                    primaryTypographyProps={{
                    fontSize: 13.5,
                    fontWeight: selectedRoleId === null ? 700 : 500,
                    whiteSpace: 'nowrap'
                    }}
                />
                <Chip
                    label={totalUsersCount}
                    size="small"
                    sx={{
                    height: 20,
                    fontSize: 11,
                    backgroundColor: selectedRoleId === null ? '#1976d2' : '#e0e0e0',
                    color: selectedRoleId === null ? '#fff' : '#424242'
                    }}
                />
                </ListItemButton>

                <Divider sx={{ my: 0.5 }} />

                {/* Listado dinámico de roles ordenados por ID asc */}
                {rolesSummary.map((role) => (
                <ListItemButton
                    key={role.id}
                    selected={selectedRoleId === role.id}
                    onClick={() => handleRoleSelect(role.id)}
                    sx={{
                    py: 1.1,
                    px: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    '&.Mui-selected': { backgroundColor: '#e3f2fd', borderLeft: '4px solid #1976d2' }
                    }}
                >
                    <ListItemText
                    primary={role.name}
                    primaryTypographyProps={{
                        fontSize: 13,
                        fontWeight: selectedRoleId === role.id ? 700 : 400
                    }}
                    />
                    <Chip
                    label={role.userCount}
                    size="small"
                    sx={{
                        height: 20,
                        fontSize: 11,
                        backgroundColor: selectedRoleId === role.id ? '#1976d2' : '#e0e0e0',
                        color: selectedRoleId === role.id ? '#fff' : '#424242'
                    }}
                    />
                </ListItemButton>
                ))}
            </List>
            </Paper>
        </Grid>

        {/* PANEL DERECHO: TABLA PRINCIPAL */}
        <Grid item xs={12} md={8.5} lg={9}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
            {/* Buscador y Resumen */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <TextField
                size="small"
                placeholder="Buscar por usuario, nombre, correo..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                    startAdornment: (
                    <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                    )
                }}
                sx={{ width: { xs: '100%', sm: 350 } }}
                />

                <Typography variant="body2" color="text.secondary">
                Mostrando <b>{filteredUsers.length}</b> usuarios{' '}
                {selectedRoleId !== null && (
                    <span>
                    (Filtrado por: <b>{rolesSummary.find((r) => r.id === selectedRoleId)?.name}</b>)
                    </span>
                )}
                </Typography>
            </Box>

            {/* DataGrid */}
            <Box sx={{ height: 600, width: '100%' }}>
                <DataGrid
                rows={filteredUsers}
                columns={columns}
                loading={loading}
                getRowId={(row) => row.id}
                pageSizeOptions={[10, 25, 50]}
                initialState={{
                    pagination: { paginationModel: { pageSize: 10, page: 0 } }
                }}
                columnVisibilityModel={{
                    roleName: selectedRoleId === null
                }}
                disableRowSelectionOnClick
                sx={{
                    border: 'none',
                    '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#fafafa',
                    fontWeight: 700
                    }
                }}
                />
            </Box>
            </Paper>
        </Grid>
        </Grid>

      {/* ========================================================================= */}
      {/* MODAL CREAR / EDITAR USUARIO                                              */}
      {/* ========================================================================= */}
      <Dialog
        open={openModal}
        onClose={() => !submitting && setOpenModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: "920px", // Ancho fijo óptimo para escritorio
            borderRadius: 2,
          },
        }}
        scroll="paper"
      >
        <DialogTitle
          sx={{
            backgroundColor: "#1976d2",
            color: "white",
            fontWeight: 600,
            py: 1.8,
          }}
        >
          {isEditing ? `Editar Usuario: ${username}` : "Nuevo Usuario"}
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          {/* ======================================================= */}
          {/* SECCIÓN 1: DATOS GENERALES                              */}
          {/* ======================================================= */}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: "#1976d2",
              mb: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <PersonIcon fontSize="small" /> 1. Datos Generales
          </Typography>

          <Grid container spacing={2}>
            {/* Nombres */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Primer Nombre *"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Segundo Nombre"
                value={secondName}
                onChange={(e) => setSecondName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Tercer Nombre"
                value={thirdName}
                onChange={(e) => setThirdName(e.target.value)}
              />
            </Grid>

            {/* Apellidos */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Primer Apellido *"
                value={firstSurname}
                onChange={(e) => setFirstSurname(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Segundo Apellido"
                value={secondSurname}
                onChange={(e) => setSecondSurname(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Apellido de Casada"
                value={marriedSurname}
                onChange={(e) => setMarriedSurname(e.target.value)}
              />
            </Grid>

            {/* Contacto */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="email"
                label="Correo Electrónico"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Teléfono"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </Grid>

            {/* Institución y Puesto */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" sx={{ width: "100%" }}>
                <InputLabel id="institution-label" shrink>
                  Institución
                </InputLabel>
                <Select
                  labelId="institution-label"
                  label="Institución"
                  displayEmpty
                  fullWidth
                  value={selectedInstitutionId}
                  onChange={(e) => {
                    setSelectedInstitutionId(e.target.value);
                    setSelectedJobId("");
                  }}
                  sx={{ width: "100%" }}
                >
                  <MenuItem value="">
                    <em>-- Todas las Instituciones --</em>
                  </MenuItem>
                  {catalogs.institutions.map((inst) => (
                    <MenuItem key={inst.id} value={inst.id}>
                      {inst.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                size="small"
                required
                sx={{ width: "100%" }}
              >
                <InputLabel id="job-label" shrink>
                  Puesto de Trabajo *
                </InputLabel>
                <Select
                  labelId="job-label"
                  label="Puesto de Trabajo *"
                  displayEmpty
                  fullWidth
                  value={selectedJobId}
                  onChange={(e) => setSelectedJobId(e.target.value)}
                  sx={{ width: "100%" }}
                >
                  <MenuItem value="" disabled>
                    <em>-- Por favor seleccione un puesto --</em>
                  </MenuItem>
                  {filteredJobs.map((j) => (
                    <MenuItem key={j.id} value={j.id}>
                      {j.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* ======================================================= */}
          {/* SECCIÓN 2: DATOS DE ACCESO                              */}
          {/* ======================================================= */}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: "#1976d2",
              mb: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <SecurityIcon fontSize="small" /> 2. Datos de Acceso
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Nombre de Usuario *"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="password"
                label={isEditing ? "Contraseña (Opcional)" : "Contraseña *"}
                placeholder={isEditing ? "Dejar en blanco para conservar" : ""}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                helperText={
                  isEditing ? "Dejar en blanco para conservar la actual" : ""
                }
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="estado-label">Estado</InputLabel>
                <Select
                  labelId="estado-label"
                  label="Estado"
                  value={enabled}
                  onChange={(e) => setEnabled(Number(e.target.value))}
                >
                  <MenuItem value={1}>Habilitado</MenuItem>
                  <MenuItem value={0}>Deshabilitado</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth size="small" required sx={{ width: '100%' }}>
                <InputLabel id="role-select-label" shrink>Rol del Usuario *</InputLabel>
                <Select
                  labelId="role-select-label"
                  label="Rol del Usuario *"
                  displayEmpty
                  fullWidth
                  value={selectedRoleIdForm}
                  onChange={handleRoleFormChange}
                  sx={{ width: '100%' }}
                >
                  <MenuItem value="" disabled>
                    <em>-- Por favor seleccione un rol --</em>
                  </MenuItem>
                  {catalogs.roles.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      <b>{r.id}</b> - {r.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* ======================================================= */}
            {/* ELEMENTOS GEOGRÁFICOS DINÁMICOS                         */}
            {/* ======================================================= */}

            {/* Rol 1 (Admin), Rol 2 (Nacional), Rol 7 (Consulta) */}
            {[1, 2, 7].includes(Number(selectedRoleIdForm)) && (
              <Grid item xs={12}>
                <Alert severity="info" icon={<PublicIcon fontSize="inherit" />}>
                  Este rol tiene cobertura <b>Nacional completa</b>. Se le
                  asignarán automáticamente los 22 departamentos del país.
                </Alert>
              </Grid>
            )}

            {/* Rol 3: Regional */}
            {Number(selectedRoleIdForm) === 3 && (
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, backgroundColor: "#fcfcfc", width: "100%" }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, mb: 1 }}
                  >
                    Departamentos Asignados (Seleccione entre 2 y 21
                    departamentos):
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1.5 }}
                  >
                    Seleccionados: {selectedDeptosRegional.length} de 22
                  </Typography>

                  <Grid container spacing={1}>
                    {catalogs.departments.map((dept) => (
                      <Grid item xs={12} sm={6} md={4} key={dept.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedDeptosRegional.includes(dept.id)}
                              onChange={() =>
                                handleDeptoRegionalToggle(dept.id)
                              }
                            />
                          }
                          label={
                            <Typography variant="body2">{dept.name}</Typography>
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>
            )}

            {/* Rol 4: Departamental */}
            {Number(selectedRoleIdForm) === 4 && (
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  size="small"
                  required
                  sx={{ width: "100%" }}
                >
                  <InputLabel id="depto-single-label" shrink>
                    Departamento Asignado *
                  </InputLabel>
                  <Select
                    labelId="depto-single-label"
                    label="Departamento Asignado *"
                    displayEmpty
                    value={selectedDeptoSingle}
                    onChange={(e) => setSelectedDeptoSingle(e.target.value)}
                  >
                    <MenuItem value="" disabled>
                      <em>-- Por favor seleccione un departamento --</em>
                    </MenuItem>
                    {catalogs.departments.map((dept) => (
                      <MenuItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Rol 5: Intermunicipal */}
            {Number(selectedRoleIdForm) === 5 && (
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      size="small"
                      required
                      sx={{ width: "100%" }}
                    >
                      <InputLabel id="filter-depto-inter-label" shrink>
                        Departamento *
                      </InputLabel>
                      <Select
                        labelId="filter-depto-inter-label"
                        label="Departamento *"
                        displayEmpty
                        value={filterDeptoForMuni}
                        onChange={(e) => {
                          setFilterDeptoForMuni(e.target.value);
                          setSelectedMunisMultiple([]);
                        }}
                      >
                        <MenuItem value="" disabled>
                          <em>-- Por favor seleccione un departamento --</em>
                        </MenuItem>
                        {catalogs.departments.map((dept) => (
                          <MenuItem key={dept.id} value={dept.id}>
                            {dept.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {filterDeptoForMuni && (
                    <Grid item xs={12}>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, backgroundColor: "#fcfcfc", width: "100%" }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, mb: 1 }}
                        >
                          Municipios de{" "}
                          {
                            catalogs.departments.find(
                              (d) => d.id === Number(filterDeptoForMuni),
                            )?.name
                          }{" "}
                          (Elija 1 o más):
                        </Typography>
                        <Grid
                          container
                          spacing={1}
                          sx={{ maxHeight: 220, overflowY: "auto" }}
                        >
                          {filteredMunicipalities.map((muni) => (
                            <Grid item xs={12} sm={6} md={4} key={muni.id}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={selectedMunisMultiple.includes(
                                      muni.id,
                                    )}
                                    onChange={() =>
                                      handleMuniMultipleToggle(muni.id)
                                    }
                                  />
                                }
                                label={
                                  <Typography variant="body2">
                                    {muni.name}
                                  </Typography>
                                }
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )}

            {/* Rol 6: Municipal */}
            {Number(selectedRoleIdForm) === 6 && (
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      required
                      sx={{ width: "100%" }}
                    >
                      <InputLabel id="filter-depto-muni-label" shrink>
                        Departamento *
                      </InputLabel>
                      <Select
                        labelId="filter-depto-muni-label"
                        label="Departamento *"
                        displayEmpty
                        value={filterDeptoForMuni}
                        onChange={(e) => {
                          setFilterDeptoForMuni(e.target.value);
                          setSelectedMuniSingle("");
                        }}
                      >
                        <MenuItem value="" disabled>
                          <em>-- Por favor seleccione un departamento --</em>
                        </MenuItem>
                        {catalogs.departments.map((dept) => (
                          <MenuItem key={dept.id} value={dept.id}>
                            {dept.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      disabled={!filterDeptoForMuni}
                      required
                      sx={{ width: "100%" }}
                    >
                      <InputLabel id="muni-single-label" shrink>
                        Municipio Asignado *
                      </InputLabel>
                      <Select
                        labelId="muni-single-label"
                        label="Municipio Asignado *"
                        displayEmpty
                        value={selectedMuniSingle}
                        onChange={(e) => setSelectedMuniSingle(e.target.value)}
                      >
                        <MenuItem value="" disabled>
                          <em>
                            {filterDeptoForMuni
                              ? "-- Por favor seleccione un municipio --"
                              : "-- Seleccione primero un depto --"}
                          </em>
                        </MenuItem>
                        {filteredMunicipalities.map((muni) => (
                          <MenuItem key={muni.id} value={muni.id}>
                            {muni.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            )}

            {/* Checkboxes de Aplicaciones */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, mt: 1, width: "100%" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Aplicaciones con Acceso Autorizado:
                </Typography>
                <FormGroup row>
                  {catalogs.applications.map((app) => (
                    <FormControlLabel
                      key={app.id}
                      control={
                        <Checkbox
                          checked={selectedAppIds.includes(app.id)}
                          onChange={() => handleAppToggle(app.id)}
                        />
                      }
                      label={<b>{app.name}</b>}
                      sx={{ mr: 3 }}
                    />
                  ))}
                </FormGroup>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setOpenModal(false)}
            disabled={submitting}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveUser}
            disabled={submitting}
            sx={{ backgroundColor: "#1976d2", textTransform: "none", px: 3 }}
          >
            {submitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : isEditing ? (
              "Guardar Cambios"
            ) : (
              "Crear Usuario"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de notificaciones */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4500}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast({ ...toast, open: false })}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}